import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, AlertCircleIcon, KeyIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from './providers/ThemeProvider';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    currentUser,
    logout,
    userType
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [currentStep, setCurrentStep] = useState<string>('');
  const [success, setSuccess] = useState(false);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsDeleting(false);
      setError('');
      setPassword('');
      setCurrentStep('');
      setSuccess(false);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  // Authenticate user with password
  const authenticateUser = async () => {
    if (!currentUser || !currentUser.email) {
      setError('Unable to authenticate. Please try logging in again.');
      return false;
    }
    if (!password.trim()) {
      setError('Please enter your password to confirm account deletion');
      return false;
    }
    try {
      setCurrentStep('Authenticating your account...');
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      console.log('[Delete Account] Successfully reauthenticated user');
      return true;
    } catch (authError: any) {
      console.error('[Delete Account] Authentication error:', authError);
      if (authError.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (authError.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (authError.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (authError.code === 'auth/invalid-credential') {
        setError('Invalid credentials. Please try again with the correct password.');
      } else {
        setError(authError.message || 'Authentication failed. Please try again.');
      }
      return false;
    }
  };
  // Delete Firestore user documents
  const deleteFirestoreData = async (uid: string, type: string) => {
    try {
      console.log('[Delete Account] Starting Firestore deletion for:', uid);
      setCurrentStep('Deleting account data...');
      // Delete from users collection
      try {
        const userRef = doc(db, 'users', uid);
        await deleteDoc(userRef);
        console.log('[Delete Account] Deleted users/' + uid);
      } catch (err) {
        console.error('[Delete Account] Failed to delete users/' + uid + ':', err);
        // Continue - document might not exist
      }
      // Delete from role-specific collection
      if (type === 'teacher') {
        try {
          const teacherRef = doc(db, 'teachers', uid);
          await deleteDoc(teacherRef);
          console.log('[Delete Account] Deleted teachers/' + uid);
        } catch (err) {
          console.error('[Delete Account] Failed to delete teachers/' + uid + ':', err);
          // Continue - document might not exist
        }
      } else if (type === 'student') {
        try {
          const studentRef = doc(db, 'students', uid);
          await deleteDoc(studentRef);
          console.log('[Delete Account] Deleted students/' + uid);
        } catch (err) {
          console.error('[Delete Account] Failed to delete students/' + uid + ':', err);
          // Continue - document might not exist
        }
      }
      console.log('[Delete Account] Firestore deletion complete');
    } catch (error) {
      console.error('[Delete Account] Firestore deletion error:', error);
      // Don't throw - we'll still delete the auth user
    }
  };
  // Main delete account function
  const handleDeleteAccount = async () => {
    if (!currentUser) {
      setError('No user logged in');
      return;
    }
    setIsDeleting(true);
    setError('');
    setCurrentStep('Preparing to delete account...');
    try {
      // Step 1: Authenticate the user
      const isAuth = await authenticateUser();
      if (!isAuth) {
        setIsDeleting(false);
        return;
      }
      const uid = currentUser.uid;
      const type = userType || 'student';
      // Step 2: Delete Firestore data BEFORE deleting auth
      await deleteFirestoreData(uid, type);
      // Step 3: Delete Firebase Auth user
      setCurrentStep('Deleting your account...');
      await deleteUser(currentUser);
      console.log('[Delete Account] Successfully deleted user from Firebase Auth');
      // Step 4: Clean up local storage
      cleanupLocalStorage();
      // Step 5: Show success and redirect
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (error: any) {
      console.error('[Delete Account] Error:', error);
      if (error.code === 'auth/requires-recent-login') {
        setError('For security, please log out and log back in before trying again.');
      } else {
        setError(error.message || 'Failed to delete account. Please try again.');
      }
      setIsDeleting(false);
    }
  };
  const cleanupLocalStorage = () => {
    console.log('[Delete Account] Cleaning up localStorage');
    // Clear all user-related data
    const keysToRemove = ['studentEmail', 'studentName', 'studentInstrument', 'studentLessonType', 'studentZipCode', 'studentPassword', 'studentFirstName', 'studentLastName', 'teacherEmail', 'teacherName', 'teacherPassword', 'teacherAvailability', 'teacherProfile', 'teacherFirstName', 'teacherLastName', 'teacherLocation', 'teacherCity', 'teacherState', 'userType', 'teacherUsers'];
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    // Clear Firebase auth token
    const firebaseKey = `firebase:authUser:AIzaSyBY4BsIdW0ljOgb-vz-Wq2jnNfwxyRW7xo:[DEFAULT]`;
    localStorage.removeItem(firebaseKey);
    console.log('[Delete Account] localStorage cleanup complete');
  };
  return <div className="fixed inset-0 flex items-center justify-center z-[9999]" data-id="element-1167">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/70" onClick={!isDeleting ? onClose : undefined} aria-hidden="true" data-id="element-1168"></div>

      {/* Modal */}
      <div className={`${isDark ? 'bg-navy-900' : 'bg-white'} rounded-xl max-w-md w-full shadow-xl relative z-[10000] mx-4 my-auto`} data-id="element-1169">
        <div className="p-6" data-id="element-1170">
          {success ? <div className="text-center py-4" data-id="element-1171">
              <div className="bg-green-600/20 rounded-full p-3 mx-auto mb-4 w-16 h-16 flex items-center justify-center" data-id="element-1172">
                <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1173">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1174" />
                </svg>
              </div>
              <h2 className={`text-xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-2`} data-id="element-1175">
                Account Deleted Successfully
              </h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-center`} data-id="element-1176">
                You will be redirected to the home page...
              </p>
            </div> : <>
              <div className="flex items-center justify-center mb-4" data-id="element-1177">
                <div className="bg-red-600/20 rounded-full p-3" data-id="element-1178">
                  <AlertTriangleIcon className="h-6 w-6 text-red-500" data-id="element-1179" />
                </div>
              </div>

              <h2 className={`text-xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-4`} data-id="element-1180">
                Are you sure you want to delete your account?
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-center mb-6`} data-id="element-1181">
                This action cannot be undone. All your data will be permanently
                removed.
              </p>

              {error && <div className={`${isDark ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-6 flex items-start`} data-id="element-1182">
                  <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1183" />
                  <p className={`${isDark ? 'text-red-300' : 'text-red-700'} text-sm`} data-id="element-1184">
                    {error}
                  </p>
                </div>}

              {/* Show progress if deleting */}
              {isDeleting && <div className={`${isDark ? 'bg-navy-800/60 border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4 mb-6`} data-id="element-1185">
                  <div className="flex items-center" data-id="element-1186">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1187">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1188"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1189"></path>
                    </svg>
                    <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1190">
                      {currentStep}
                    </p>
                  </div>
                </div>}

              {/* Password Entry Field */}
              <div className="space-y-4" data-id="element-1191">
                <div data-id="element-1192">
                  <label htmlFor="password" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-1193">
                    Enter your password to confirm
                  </label>
                  <div className="relative" data-id="element-1194">
                    <KeyIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1195" />
                    <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && password) {
                    handleDeleteAccount();
                  }
                }} className={`w-full pl-10 pr-4 py-3 ${isDark ? 'bg-white/5 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg focus:ring-deep-pink focus:border-deep-pink`} placeholder="Your password" autoComplete="current-password" disabled={isDeleting} data-id="element-1196" />
                  </div>
                </div>

                <div className="flex space-x-3" data-id="element-1197">
                  <button onClick={onClose} className={`flex-1 ${isDark ? 'bg-transparent hover:bg-gray-700 text-white border-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'} py-2 px-4 rounded-lg transition-colors border`} disabled={isDeleting} data-id="element-1198">
                    Cancel
                  </button>
                  <button onClick={handleDeleteAccount} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors flex justify-center items-center" disabled={isDeleting || !password} data-id="element-1199">
                    {isDeleting ? <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1200">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1201"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1202"></path>
                        </svg>
                        Deleting...
                      </> : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            </>}
        </div>
      </div>
    </div>;
};